import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import * as React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { EntityComments } from "@/shared/components/comments";
import {
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components/index.tsx";
import { TagList } from "@/shared/components/TagList";
import { FormattedDateTime } from "@/shared/datetime";
import { CollectionRecord } from "@/shared/domain/collections";

import styles from "./CollectionDetails.module.css";
import { CollectionTypeChip } from "./CollectionTypeChip.tsx";
import { EditableCollectionDescription } from "./EditableCollectionDescription.tsx";
import { EditableCollectionName } from "./EditableCollectionName.tsx";
import {
  CollectionTab,
  useActiveCollectionTab,
} from "./useActiveCollectionTabFromUrl";

interface CollectionDetailsProps {
  collection: CollectionRecord;
}

function totalItemCount(resources: CollectionRecord["resources"]): number {
  return Object.values(resources).reduce((sum, arr) => sum + arr.length, 0);
}

export const CollectionDetails: React.FC<CollectionDetailsProps> = ({
  collection,
}) => {
  const activeTab = useActiveCollectionTab();
  const location = useLocation();

  const itemCount = totalItemCount(collection.resources);

  return (
    <div className={styles.root}>
      {/* ── Header + Tab bar ── */}
      <div className={styles.headerSection}>
        <div className={styles.header}>
          {/* Left: name / description / metadata / tags */}
          <div className={styles.headerLeft}>
            <div className={styles.titleRow}>
              <FolderOutlinedIcon
                className={styles.folderIcon}
                fontSize="small"
              />
              <EditableCollectionName
                name={collection.name ?? ""}
                collectionId={collection.collection_id}
                orgId={collection.org_id}
              />
              <CollectionTypeChip resourceType={collection.resource_type} />
            </div>

            <EditableCollectionDescription
              description={collection.description ?? ""}
              collectionId={collection.collection_id}
              orgId={collection.org_id}
            />

            <div className={styles.metadataRow}>
              <PersonOutlinedIcon className={styles.metadataIcon} />
              <span className={styles.metadataLabel}>Created By:</span>
              <span className={styles.metadataText}>
                {collection.created_by}
              </span>
              <span className={styles.metadataDot}>·</span>
              <CalendarTodayOutlinedIcon className={styles.metadataIcon} />
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataText}>
                <FormattedDateTime value={collection.created} time="none" />
              </span>
              {itemCount > 0 && (
                <>
                  <span className={styles.metadataDot}>·</span>
                  <span className={styles.metadataText}>
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </span>
                </>
              )}
            </div>

            {collection.tags.length > 0 && (
              <div className={styles.tagsRow}>
                <TagList tags={collection.tags} />
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className={styles.tabBar}>
          <RobotoStyledTabs value={activeTab}>
            <RobotoStyledTab
              value={CollectionTab.Items}
              label="Items"
              icon={
                <FormatListBulletedOutlinedIcon style={{ fontSize: "1rem" }} />
              }
              iconPosition="start"
              component={Link}
              to={{ pathname: CollectionTab.Items, search: location.search }}
              replace
            />
            <RobotoStyledTab
              value={CollectionTab.Details}
              label="Details"
              icon={<InfoOutlinedIcon style={{ fontSize: "1rem" }} />}
              iconPosition="start"
              component={Link}
              to={{ pathname: CollectionTab.Details, search: location.search }}
              replace
            />
            <RobotoStyledTab
              value={CollectionTab.Activity}
              label="Activity"
              icon={<AccessTimeOutlinedIcon style={{ fontSize: "1rem" }} />}
              iconPosition="start"
              component={Link}
              to={{
                pathname: CollectionTab.Activity,
                search: location.search,
              }}
              replace
            />
            <RobotoStyledTab
              value={CollectionTab.Permissions}
              label="Permissions"
              icon={<ShieldOutlinedIcon style={{ fontSize: "1rem" }} />}
              iconPosition="start"
              component={Link}
              to={{
                pathname: CollectionTab.Permissions,
                search: location.search,
              }}
              replace
            />
          </RobotoStyledTabs>
        </div>
      </div>

      {/* Items tab needs flex-fill + internal scroll so the table fills the
          viewport. Other tabs use plain padding so <EntityComments> sits
          directly under the content rather than being pushed to the bottom. */}
      <div
        className={
          activeTab === CollectionTab.Items
            ? styles.itemsOutlet
            : styles.tabPanel
        }
      >
        <Outlet context={collection} />
      </div>

      <div className={styles.commentsSection}>
        <EntityComments
          entityType="collection"
          entityId={collection.collection_id}
        />
      </div>
    </div>
  );
};
